// © Microsoft Corporation. All rights reserved.

import { KeyValueArray, KeyValuePair, Utility } from "./Utility";
import { findIndex, isArray, isEmpty, isString, trimEnd, trimStart } from "lodash-es";

import { StringMap } from "../types";
import { getLocationOrigin } from "@msnews/isomorphic-util";

export namespace UrlUtility {

    /**
     * Characters that, if present on a url, will make prime routing misbehave
     *
     * These are the characters we know cause problems:
     *  - spaces
     *  - slashes
     *  - punctuation: !?.:
     *  - ampersands
     *
     * As we don't know all the offending characters, we are just removing every character that is not alphanumeric
     */
    const primeForbiddenCharacters: RegExp = /[^A-Za-z0-9]+/g;

    /**
     * Parse parameters from the given URL
     * @param {string} url - the URL to parse for parameters
     * @param {boolean} decodeComponents - flag to indicate whether the query param key and value should be URL decoded
     * @returns {KeyValueArray<string, string>} - the parsed query key/value collection
     */
    export function getParamsFromUrl(url: string, decodeComponents: boolean = false): KeyValueArray<string, string> {
        if (!isString(url)) {
            return null;
        }

        let queryString: string = (url || "").split("?")[1];
        return getParams(queryString, decodeComponents);
    }

    /**
     * Decode the given URL encoded string that may contain plus signs
     * @param {string} val - The value to decode.
     * @returns {string} - the decoded value
     */
    function decodeURIComponentWithPlusSign(val: string): string {
        val = val.replace(/\+/g, " ");
        val = decodeURIComponent(val);
        return val;
    }

    /**
     * Encode uri components in a way that won't break prime routing system
     * @param uriComponent the uriComponent to be encoded
     */
    export function encodeURIComponentForPrime(uriComponent: string): string {
        return encodeURIComponent(uriComponent.replace(primeForbiddenCharacters, "-"));
    }

    /**
     * Parse parameters from the given query string
     * @param {string} queryString - the query string to parse for parameters
     * @param {boolean} decodeComponents - flag to indicate whether the query param key and value should be URL decoded
     * @returns {KeyValueArray<string, string>} - the parsed query key/value collection
     */
    export function getParams(queryString: string, decodeComponents: boolean = false): KeyValueArray<string, string> {
        let params: KeyValueArray<string, string> = [];
        // Remove the leading ? if it is present
        if (queryString && queryString.startsWith("?")) {
            queryString = queryString.substring(1);
        }

        if (queryString) {
            // remove hash part.
            queryString = queryString.split("#")[0];
            let queryArray = queryString.split("&");
            for (let ndx = 0; ndx < queryArray.length; ndx++) {
                let parts = queryArray[ndx].split("=");
                let queryKey = parts[0];
                let queryValue = parts[1];

                if (decodeComponents) {
                    queryKey = decodeURIComponentWithPlusSign(queryKey);
                    if (queryValue) {
                        queryValue = decodeURIComponentWithPlusSign(queryValue);
                    }
                }

                // Don't allow for duplicate query string parameter keys. Take the last one
                let newParam: KeyValuePair<string, string> = { key: queryKey, value: queryValue };
                let existingParam = findIndex(params, (entry) => entry.key === newParam.key);
                if (existingParam === -1) {
                    params.push(newParam);
                } else {
                    params[existingParam] = newParam;
                }
            }
        }
        return params;
    }

    /**
     * Parse parameters from the given query string, allowing for multiple item=key:value
     * @param {string} queryString - the query string to parse for parameters
     * @returns {KeyValueArray<string, string>} - the parsed query key/value collection
     */
    export function getParamsWithItems(queryString: string): KeyValueArray<string, string> {
        let params: KeyValueArray<string, string> = [];

        if (queryString) {
            // Remove the leading ? if it is present
            if (queryString.startsWith("?")) {
                queryString = queryString.substring(1);
            }

            // split on the hashes.
            queryString = queryString.split("#")[0];
            let queryArray = queryString.split("&");
            for (let ndx = 0; ndx < queryArray.length; ndx++) {
                let parts = queryArray[ndx].split("=");
                let queryKey = parts[0];
                let queryValue = parts[1];

                // If the query key is "item", and the query values is key:value, then convert the
                // value into the actual key/value
                if (queryKey.toLowerCase() === "item") {
                    let queryItems = queryValue.split(":");
                    if (queryItems.length === 2) {
                        queryKey = queryItems[0];
                        queryValue = queryItems[1];
                    }
                }

                // Don't allow for duplicate query string parameter keys. Take the last one
                let newParam: KeyValuePair<string, string> = { key: queryKey, value: queryValue };
                let existingParam = params.findIndex((entry) => entry.key === newParam.key);
                if (existingParam === -1) {
                    params.push(newParam);
                } else {
                    params[existingParam] = newParam;
                }
            }
        }
        return params;
    }

    /**
     * Utility method to extract query string param in url.
     *
     * @export
     * @param {string} name - query param name
     * @returns {string} - query param value
     */
    export function getQueryParameterByName(name: string, href: string): string {

        // TODO: exposing this method for accessing a single parameter can be used
        //  extremely inefficiently (for example if invoked multiple times to get parameters
        //  from a single URL). Instead we should deprecate this and export getParamsObj
        //  and use that throughout.

        let value = getParamsObj(href)[name];
        return (value !== undefined)
            ? value
            : null;
    }

    /**
     * Check if given url string is a valid url.
     * Returns true when url string is a valid url, otherwise false.
     *
     * @export
     * @param url {string} - Url string value to check
     * @param validateScheme {boolean} - Should scheme validation be forced
     * @return {boolean}
     */
    export function isValidUrl(url: string, validateScheme: boolean): boolean {
        return (isString(url) &&
            (url.length > 0) &&
            (!validateScheme ||
                validateScheme && url.indexOf("http") === 0));
    }

    /**
     * Converts a keyValueArray<string, string> into a set of query string parameters
     *
     * @export
     * @param {any} - params to be converted
     * @return {string}
     */
    export function keyValueArrayToQueryString(params: KeyValueArray<string, string>): string {
        // Validate we have a nonempty array
        if (!isArray(params) || isEmpty(params)) {
            return "";
        }

        // Filter for valid keyValue entries, and then map them into key==value strings
        // joined with an &
        const queryString: string = params.filter(kv =>
            Utility.isKeyValuePair<string, string>(
                kv,
                (key) => typeof (key) === "string",
                (value) => typeof (value) === "string"))
            .map(kv => kv.key + "=" + kv.value)
            .join("&");

        return queryString;
    }

    /**
     * Helper method to parse the given URLs query parameters to a lookup dictionary
     * @param url - the URL string with the query params to parse
     * @returns The {StringMap} containing the key/value pairs, or empty object.
     */
    function getParamsObj(url: string): StringMap {
        let paramsObj = {};
        let paramArray = getParamsFromUrl(url, true);
        if (paramArray && paramArray.length) {
            paramArray.forEach(keyValuePair =>
                paramsObj[keyValuePair.key] = keyValuePair.value);
        }
        return paramsObj;
    }

    /**
     * For the given `url`, it appends the `subPath` to it.
     * It takes propers care to forward slashes characters
     * at the end of `url` and at the start of `subPath` parameters
     * @param url a valid url
     * @param subPath  the path which is appended to `url`
     * @returns an URL object which is the `url` appended by `subPath`
     */
    export function joinSubpathToURL(url: URL, subPath: string): URL {
        const href = url.href;
        const trimmedEndSlashFromHref = trimEnd(href, "/");
        const trimmedStartSlashFromSubpath = trimStart(subPath, "/");
        return new URL(`${trimmedEndSlashFromHref}/${trimmedStartSlashFromSubpath}`);
    }

    /**
     * Updates a search parameter in the given url to map to a new value
     * Does not modify the url if the given search param doesn't exist
     * @param url the url to update
     * @param key the name of the search param to be updated
     * @param value the new value for the given search param
     * @returns {URL} the updated URL
     */
    export function updateSearchParam(url: URL, key: string, value: string): URL {
        // Note that the searchParams data member may not be compatible with older browser versions
        // see https://caniuse.com/#search=searchparams
        if (url && url.searchParams && url.searchParams.get(key)) {
            url.searchParams.set(key, value);
        }

        return url;
    }

    /**
     * Appends a search parameter to the given url
     * @param url the url to modify
     * @param key the name of the search param to be appended
     * @param value the new value for the given search param
     * @returns {URL} the modified URL
     */
    export function appendSearchParam(url: URL, key: string, value: string, fallback?: boolean): URL {
        if (url) {
            if (url.searchParams || fallback) {
                url.searchParams.append(key, value);
            } else {
                // TODO Bug 22390312: [Peregrine] UrlUtility appendSearchParam needs more robust polyfill
                url = new URL(url.href + (url.search.indexOf("?") >= 0 ? "&" : "?") + key + "=" + value);
            }
        }

        return url;
    }

    /**
     * Returns an absolute URL if a relative on is passed in, or returns the passed in URL if already absolute
     * @param partialUrl The relative or absolute URL
     * @returns {string} the absolute URL
     */
    export function getAbsoluteUrl(partialUrl: string): string {
        if (partialUrl.indexOf('://') > 0 || partialUrl.indexOf('//') === 0) {
            return partialUrl;
        } else {
            return getLocationOrigin() + partialUrl;
        }
    }
}